import { createContext, useCallback, useEffect, useReducer, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

import Loader from 'components/Loader';
import axios from 'utils/axios';
import { DashboardApi, LoginApi, MiddleWareGetApi, MiddleWarePostApi } from '../services/apiService';
import { AuthApi } from '../apis/authapi';
import { DecryptJweToken, DecryptText, EncryptText } from '../services/EncDecService';
import { fetchGeoLocation } from '../services/geoLocation';
import { showNotification } from '../services/notification';
import { sessionDataDec, sessionDataEnc } from '../services/sessionServiceEndDec';

// ─── Constants ────────────────────────────────────────────────────────────────

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  userData: null
};

// ─── Session Helpers ──────────────────────────────────────────────────────────

const setSession = (token) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    sessionStorage.clear();
    localStorage.clear();
    delete axios.defaults.headers.common.Authorization;
  }
};

const getStoredDashboardData = () => {
  try {
    return JSON.parse(sessionDataDec('dashboardData')) ?? null;
  } catch {
    return null;
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [loginLoader, setLoginLoader] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  // ─── Bootstrap: restore session from sessionStorage on mount ──────────────
  useEffect(() => {
    const dashboardData = getStoredDashboardData();

    dispatch({
      type: dashboardData ? LOGIN : LOGOUT,
      payload: {
        isLoggedIn: !!dashboardData,
        userData: dashboardData ?? null
      }
    });
  }, []); // runs once on mount — no need to track decodedData in state

  // ─── Auth Helpers ──────────────────────────────────────────────────────────

  /**
   * Decodes the stored JWE token and returns the mobile number.
   */
  const getDecodedMobileNumber = useCallback(async () => {
    const token = sessionDataDec('token');
    const jweToken = await DecryptJweToken(token);
    const { mobileNumber } = jwtDecode(jweToken);
    return mobileNumber;
  }, []);

  /**
   * Checks whether accessStatus and kycStatus arrays are identical (order-independent).
   */
  const checkStatusEquality = useCallback((accessStatus, kycStatus) => {
    if (!Array.isArray(accessStatus) || !Array.isArray(kycStatus)) return false;
    if (accessStatus.length !== kycStatus.length) return false;
    const sort = (arr) => [...arr].sort((a, b) => a - b);
    return JSON.stringify(sort(accessStatus)) === JSON.stringify(sort(kycStatus));
  }, []);

  // ─── Core Flows ────────────────────────────────────────────────────────────

  const dashboard = useCallback(async (kycType, kycStatusArr) => {
    try {
      const res = await DashboardApi(AuthApi.url.dashboard);
      const dashboardData = { ...res.userInfo, kycType, kycStatusArr };

      sessionDataEnc('dashboardData', JSON.stringify(dashboardData));

      dispatch({
        type: LOGIN,
        payload: { isLoggedIn: true, userData: dashboardData }
      });

      showNotification('Login successful', 'success');
    } catch (err) {
      showNotification(err?.response?.data?.message ?? 'Dashboard fetch failed', 'error');
      console.error('[dashboard]', err);
    } finally {
      setLoginLoader(false);
    }
  }, []);

  const statusCheck = useCallback(async () => {
    try {
      const mobileNumber = await getDecodedMobileNumber();

      let latLong = null;
      try {
        latLong = await fetchGeoLocation();
      } catch (geoErr) {
        console.warn('[statusCheck] Geolocation unavailable:', geoErr);
      }

      const encryptedReq = await EncryptText({
        mobileNumber: mobileNumber.toString(),
        latLong,
        channel: 'WEB'
      });

      const res = await MiddleWarePostApi(AuthApi.url.user_status_check, encryptedReq);
      const decryptedRes = await DecryptText(res.data);
      const { accessStatus, kycStatus } = decryptedRes.cardDetails;

      if (checkStatusEquality(accessStatus, kycStatus)) {
        await dashboard(decryptedRes.userDetails.kycType, kycStatus);
        return '/dashboard';
      } else {
        setLoginLoader(false);
        return '/user-registration';
      }
    } catch (err) {
      setLoginLoader(false);
      showNotification(err?.response?.data?.statusDesc ?? 'Status check failed', 'error');
      console.error('[statusCheck]', err);
    }
  }, [checkStatusEquality, dashboard, getDecodedMobileNumber]);

  // ─── Public API ────────────────────────────────────────────────────────────

  const login = useCallback(async (mobileNumber, otp, randomWord) => {
    setLoginLoader(true);
    try {
      const res = await LoginApi(AuthApi.url.verifyotp, {
        mobileNumber,
        otp,
        otpParams: randomWord
      });
      sessionDataEnc('token', res.access_token);
      sessionDataEnc('refresh_token', res.refresh_token);

      // Call partner_authorization GET API
      const authRes = await MiddleWareGetApi(AuthApi.url.partner_authorization);
      if (!authRes || authRes.statusCode !== 0) {
        setLoginLoader(false);
        showNotification(authRes?.statusDesc ?? 'Partner authorization failed', 'error');
        setSession(null); // Clear session
        return null;
      }

      const nextPath = await statusCheck();
      return nextPath;
    } catch (err) {
      setLoginLoader(false);
      showNotification(err?.response?.data?.statusDesc ?? 'Login failed', 'error');
      console.error('[login]', err);
    } finally {
      // Just in case statusCheck didn't catch it
      setLoginLoader(false);
    }
  }, [statusCheck]);

  const logout = useCallback(() => {
    setLoginLoader(false);
    setSession(null);
    dispatch({
      type: LOGOUT,
      payload: { isLoggedIn: false, userData: null }
    });
  }, []);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await MiddleWareGetApi(AuthApi.url.fetch_customer_balance);
      if (res.status == 0) {
        setWalletBalance(res.balance);
        return res.balance;
      }
      return 0;
    } catch (err) {
      console.error('[fetchWallet]', err);
      return 0;
    }
  }, []);

  // Stubs retained for interface compatibility
  const register = useCallback(async () => {
    console.warn('[register] Not yet implemented');
  }, []);

  const resetPassword = useCallback(async (email) => {
    console.warn('[resetPassword] Not yet implemented for:', email);
  }, []);

  const updateProfile = useCallback(() => { }, []);

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
        loginLoader,
        walletBalance,
        fetchWallet,
        statusCheck
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;